<ng-container *transloco="let t; read: 'adminOperationDefaults'">
  <app-grid-page [title]="t('title')">
    <app-grid
      [columns]="columns()"
      [data]="pagedDefaults()"
      [rowId]="defaultId"
      [loading]="isLoading()"
      [page]="page()"
      [pageSize]="size()"
      [totalElements]="totalElements()"
      [totalPages]="totalPages()"
      [sort]="sort()"
      [searchable]="true"
      [detailEnabled]="true"
      detailMode="split"
      [detailWidth]="620"
      [selectedRow]="selectedDefault()"
      [primaryAction]="{ label: t('addDefault'), icon: 'icon-content-add24px' }"
      (sortChange)="onSortChange($event)"
      (pageChange)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)"
      (rowClick)="onRowClick(asDefault($event))"
      (primaryActionClick)="openAddDialog()"
    >
      <!-- Column: Profile — name + ops count -->
      <ng-template gridCell="profile" let-row>
        @let pr = asDefault(row);
        <div class="min-w-0">
          <span class="block truncate text-[14px] font-semibold text-ink-1">{{ pr.operationProfileId }}</span>
          <div class="mt-0.5 truncate text-[12px] text-ink-2">
            {{ t('row.operations', { count: operationsCount(pr) }) }}
          </div>
        </div>
      </ng-template>

      <!-- Column: Status — dot + word -->
      <ng-template gridCell="status" let-row>
        @if (rowFlag(asDefault(row)); as flag) {
          <span class="flex items-center gap-2 text-[14px] text-ink-1">
            <app-badge [color]="flagDot(flag)" [size]="12" />
            {{ t('flags.' + flag) }}
          </span>
        }
      </ng-template>

      <!-- Column: Power mode -->
      <ng-template gridCell="powerMode" let-row>
        @let p = asDefault(row);
        <span class="flex items-center gap-2 text-[14px] capitalize text-ink-1">
          <app-badge [color]="powerModeDot(p.powerMode)" [size]="12" />
          {{ p.powerMode.toLowerCase() }}
        </span>
      </ng-template>

      <!-- Column: Schedule -->
      <ng-template gridCell="schedule" let-row>
        <span class="truncate text-[13px] text-ink-2">{{ rowScheduleLabel(asDefault(row)) }}</span>
      </ng-template>

      <!-- Column: mini weekly coverage strip -->
      <ng-template gridCell="week" let-row>
        <div class="w-[150px]">
          <app-schedule-strip
            [bands]="defaultBands(asDefault(row))"
            [coverageBase]="true"
            [height]="34"
            [hideLabels]="true"
          />
        </div>
      </ng-template>

      <!-- Detail inspector (split pane) rendered from the component's own
           selectedDefault() so edits refresh the panel. -->
      <ng-template gridDetail let-row>
        @if (selectedDefault(); as def) {
        <div class="@container flex h-full min-h-0 flex-col">
          <div class="flex-none">
            <div class="flex items-center gap-3 border-b border-hairline px-3 py-3 @lg:px-6">
              <app-button
                variant="secondary"
                size="sm"
                iconLeft="icon-edit-pencil"
                [label]="t('actions.edit')"
                (activated)="editSelectedDefault()"
              />
              <app-button
                variant="primary"
                size="sm"
                destructive
                iconLeft="icon-delete-trash"
                [label]="t('actions.delete')"
                (activated)="deleteSelectedDefault()"
              />
            </div>

            <div class="px-3 pt-4 @lg:px-6">
              <div class="flex items-center justify-between gap-4">
                <span class="min-w-0 truncate font-display text-[28px] font-bold leading-9 text-ink-1">
                  {{ def.operationProfileId }}
                </span>
                <span class="flex flex-none items-center gap-2 text-[14px] capitalize text-ink-1">
                  <app-badge [color]="powerModeDot(def.powerMode)" [size]="12" />
                  {{ def.powerMode.toLowerCase() }}
                </span>
              </div>
              <div class="mt-1 text-[12px] text-ink-2">
                {{ t('detail.profileId', { id: def.operationProfileId } ) }} ·
                {{ t('row.operations', { count: operations().length }) }}
              </div>

              <div class="mt-3 flex h-12 items-stretch gap-0.5 overflow-x-auto border-b border-hairline">
                @for (tab of [
                  { id: 'overview', label: t('tabs.powerSettings') },
                  { id: 'operations', label: t('tabs.operations'), badge: operations().length }
                ]; track tab.id) {
                  <button
                    type="button"
                    (click)="setTab($any(tab.id))"
                    class="relative flex items-center gap-2 px-3.5"
                    [class.text-ink-1]="detailTab() === tab.id"
                    [class.text-ink-2]="detailTab() !== tab.id"
                    [class.hover:text-ink-1]="detailTab() !== tab.id"
                  >
                    @if (tab.badge !== undefined) {
                      <span
                        class="flex h-5 w-5 flex-none items-center justify-center rounded-pill border border-strong text-[11px] font-bold"
                      >
                        {{ tab.badge }}
                      </span>
                    }
                    <span class="whitespace-nowrap font-display text-[14px] font-bold uppercase tracking-[0.03em]">
                      {{ tab.label }}
                    </span>
                    @if (detailTab() === tab.id) {
                      <div class="absolute inset-x-0 -bottom-px h-1 bg-action"></div>
                    }
                  </button>
                }
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="min-h-0 flex-1 overflow-auto px-3 py-5 @lg:px-6">
            @if (detailTab() === 'overview') {
              <div class="flex flex-col gap-5">
                <!-- Battery thresholds -->
                <div class="rounded-card bg-card p-5 shadow-raised">
                  <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <span class="text-[11px] font-bold uppercase tracking-wide text-ink-3">
                      {{ t('powerThresholds') }}
                    </span>
                    <app-label-badge [dot]="def.allowWakeup ? 'green' : null">{{ def.allowWakeup ? t('wakeupAllowed') : t('wakeupBlocked') }}</app-label-badge>
                  </div>
                  <app-power-threshold-bar
                    [shutdownPct]="def.noPowerPercentage"
                    [shutdownOutPct]="def.noPowerPercentageOut"
                    [lowPct]="def.lowPowerPercentage"
                    [lowOutPct]="def.lowPowerPercentageOut"
                  />
                  <div class="mt-3 text-[12px] text-ink-2">
                    {{
                      t('thresholdSummary', {
                        lowIn: def.lowPowerPercentage,
                        lowOut: def.lowPowerPercentageOut,
                        noIn: def.noPowerPercentage,
                        noOut: def.noPowerPercentageOut
                      })
                    }}
                  </div>
                </div>

                <!-- Default values -->
                <div>
                  <div class="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-ink-3">
                    {{ t('defaultValues') }}
                  </div>
                  <div class="grid grid-cols-2 gap-px overflow-hidden rounded-card bg-hairline shadow-raised @xl:grid-cols-2">
                    @for (d of [
                      { label: t('fields.render'), value: formatInterval(def.renderInterval) },
                      { label: t('fields.connection'), value: formatInterval(def.connectionInterval) }
                    ]; track d.label) {
                      <div class="flex flex-col gap-1 bg-card px-4 py-3">
                        <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ d.label }}</span>
                        <span class="font-num text-[15px] font-semibold text-ink-1">{{ d.value }}</span>
                      </div>
                    }
                  </div>
                  <p class="mt-2 text-[12px] text-ink-2">{{ t('inheritNote') }}</p>
                </div>

                <!-- Weekly coverage -->
                <div class="rounded-card bg-card p-5 shadow-raised">
                  <div class="mb-3.5 flex items-center justify-between">
                    <span class="text-[11px] font-bold uppercase tracking-wide text-ink-3">
                      {{ t('weeklyCoverage') }}
                    </span>
                  </div>
                  <app-schedule-strip [bands]="coverageBands()" [coverageBase]="true" [height]="130" [axis]="true" />
                  <p class="mt-3.5 text-[12px] text-ink-2">{{ t('coverageNote') }}</p>
                </div>
              </div>
            } @else if (detailTab() === 'operations') {
              <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
                <span class="text-[12px] text-ink-2">{{ t('operationsNote') }}</span>
                <button
                  type="button"
                  (click)="openAddOperationDialog()"
                  class="flex items-center gap-1.5 rounded-control border border-hairline bg-card px-3 py-1.5 text-[12px] font-semibold text-ink-1 hover:bg-sunken"
                >
                  <app-icon name="icon-content-add24px" [size]="14" />
                  {{ t('actions.addOperation') }}
                </button>
              </div>

              @if (operations().length === 0) {
                <div class="flex h-40 items-center justify-center text-sm text-ink-3">
                  {{ t('noOperations') }}
                </div>
              } @else {
                <div class="flex flex-col gap-3.5">
                  @for (op of operations(); track op.operationId; let i = $index) {
                    <div class="rounded-card bg-card p-5 shadow-raised">
                      <div class="mb-3 flex items-start justify-between gap-3">
                        <div class="flex min-w-0 items-center gap-2.5">
                          <span
                            class="h-2.5 w-2.5 flex-none rounded-[3px]"
                            [style.background]="operationColor(i)"
                          ></span>
                          <div class="min-w-0">
                            <div class="truncate font-num text-[15px] font-semibold text-ink-1">
                              {{ op.operationId }}
                            </div>
                            <div class="mt-0.5 truncate text-[12px] text-ink-2">
                              {{ operationScheduleLabel(op) }}
                            </div>
                          </div>
                        </div>
                        <div class="flex flex-none items-center gap-1.5">
                          @if (op.sleep) {
                            <app-label-badge dot="blue">{{ t('flags.sleep') }}</app-label-badge>
                          }
                          @if (op.shutdown) {
                            <app-label-badge dot="red">{{ t('flags.shutdown') }}</app-label-badge>
                          }
                          @if (!op.sleep && !op.shutdown) {
                            <app-label-badge dot="green">{{ t('flags.active') }}</app-label-badge>
                          }
                          <app-button
                            variant="secondary"
                            size="sm"
                            iconLeft="icon-edit-pencil"
                            [label]="t('actions.edit')"
                            (activated)="editOperation(op)"
                          />
                          <app-button
                            variant="secondary"
                            size="sm"
                            destructive
                            iconLeft="icon-delete-trash"
                            [label]="t('actions.delete')"
                            (activated)="deleteOperation(op)"
                          />
                        </div>
                      </div>

                      <div class="flex flex-col items-stretch gap-4 @2xl:flex-row">
                        <div class="w-full flex-none @2xl:w-[240px]">
                          <app-schedule-strip
                            [days]="op.days"
                            [startTime]="op.startTime"
                            [endTime]="op.endTime"
                            [sleep]="op.sleep"
                            [shutdown]="op.shutdown"
                            [height]="82"
                          />
                        </div>

                        <div class="flex min-w-0 flex-1 flex-col gap-3">
                          <div class="flex items-center gap-2.5">
                            <span class="min-w-[78px] text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('op.battery') }}</span>
                            <div class="flex flex-wrap gap-1.5">
                              @for (s of op.batteryPowerStatusList; track s) {
                                <app-label-badge [dot]="batteryStatusDot(s)">{{ batteryStatusLabel(s) }}</app-label-badge>
                              }
                            </div>
                          </div>
                          <div class="flex items-center gap-2.5">
                            <span class="min-w-[78px] text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('illumination') }}</span>
                            <div class="flex-1"><app-illumination-bar [value]="illuminationOf(op)" /></div>
                          </div>
                          <div class="grid grid-cols-2 gap-px overflow-hidden rounded-card bg-hairline shadow-raised">
                            @for (f of [
                              { label: t('fields.render'), value: formatInterval(op.renderInterval) },
                              { label: t('fields.connection'), value: formatInterval(op.connectionInterval) }
                            ]; track f.label) {
                              <div class="flex flex-col gap-1 bg-card px-3 py-2.5">
                                <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ f.label }}</span>
                                <span class="font-num text-[14px] font-semibold text-ink-1">{{ f.value }}</span>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            }
          </div>
        </div>
        }
      </ng-template>
    </app-grid>
  </app-grid-page>

  @if (isProfileDialogOpen()) {
    <app-profile-dialog
      [profile]="editingDefault()"
      [mode]="dialogMode()"
      [busy]="isProfileSaving()"
      (save)="handleProfileSave($event)"
      (close)="closeProfileDialog()"
    />
  }

  @if (isOperationDialogOpen() && selectedDefault()) {
    <app-operation-dialog
      [operation]="editingOperation()"
      [mode]="dialogMode()"
      [profileId]="selectedDefault()!.id"
      (save)="handleOperationSave($event)"
      (close)="closeOperationDialog()"
    />
  }
</ng-container>
